import {useCallback, useEffect, useRef, useState} from 'react';
import type ErgAuthService from '@ergeon/erg-auth';

import {RecoveryFlush, RecoveryFlushRef} from './SessionRecoveryContext';

export const RECOVERY_FLUSH_TIMEOUT_MS = 10_000;

export type SignInProgress = 'idle' | 'keeping-edits' | 'edits-at-risk';

type SignInKeepingEdits = {
  recoveryFlushRef: RecoveryFlushRef;
  signInProgress: SignInProgress;
  onAuth(): void;
};

const editsKeptWithin = async (flush: RecoveryFlush, timeoutMs: number): Promise<boolean> => {
  let expiry: ReturnType<typeof setTimeout> | undefined;
  const kept = flush().then(
    () => true,
    () => false,
  );
  const gaveUp = new Promise<boolean>((resolve) => {
    expiry = setTimeout(() => resolve(false), timeoutMs);
  });
  const outcome = await Promise.race([kept, gaveUp]);
  clearTimeout(expiry);
  return outcome;
};

function useSignInKeepingEdits(authService: ErgAuthService, sessionNoticeVisible: boolean): SignInKeepingEdits {
  const recoveryFlushRef = useRef<RecoveryFlush | null>(null);
  const [signInProgress, setSignInProgress] = useState<SignInProgress>('idle');
  const noticeEpisode = useRef(0);

  useEffect(() => {
    if (sessionNoticeVisible) {
      return;
    }

    noticeEpisode.current += 1;
    setSignInProgress('idle');
  }, [sessionNoticeVisible]);

  const signInOnceEditsAreKept = useCallback(
    async (flush: RecoveryFlush) => {
      const episode = noticeEpisode.current;
      setSignInProgress('keeping-edits');
      const kept = await editsKeptWithin(flush, RECOVERY_FLUSH_TIMEOUT_MS);

      const noticeWasWithdrawn = episode !== noticeEpisode.current;
      if (noticeWasWithdrawn) {
        return;
      }

      if (kept) {
        setSignInProgress('idle');
        void authService.authenticate();
        return;
      }

      setSignInProgress('edits-at-risk');
    },
    [authService],
  );

  const onAuth = useCallback(() => {
    if (signInProgress === 'keeping-edits') {
      return;
    }

    const flush = recoveryFlushRef.current;
    const operatorAcceptedTheRisk = signInProgress === 'edits-at-risk';
    if (!flush || operatorAcceptedTheRisk) {
      void authService.authenticate();
      return;
    }

    void signInOnceEditsAreKept(flush);
  }, [authService, signInProgress, signInOnceEditsAreKept]);

  return {recoveryFlushRef, signInProgress, onAuth};
}

export default useSignInKeepingEdits;
